import { directSupabase } from '../../lib/supabaseClient';
import { getGraphClientSilent } from '../../utils/graphService';
import { outlookDateChanges, type OutlookEventTimes } from '../../utils/outlookEventDates';
import { isGraphNotFound } from './jobCardDiary';

type LinkedEventRow = {
  id: string;
  calendar_event_id?: string | null;
  event_date?: string | null;
  end_date?: string | null;
  status?: string | null;
};

/**
 * Copies Outlook date changes onto linked project_events so the Job Card and Job Records show them.
 * Only runs for a user already signed in to Microsoft; everyone else reads the saved rows.
 * `changed` when at least one row changed; `missing` lists Outlook events deleted in Outlook,
 * which are left for someone to reschedule or cancel rather than cancelled automatically.
 */
export async function syncLinkedEventDatesFromOutlook(
  rows: LinkedEventRow[],
  userId?: string | null,
): Promise<{ changed: boolean; missing: string[] }> {
  const missing: string[] = [];
  const linked = rows.filter((row) => row.calendar_event_id && row.status !== 'cancelled');
  if (!linked.length) return { changed: false, missing };
  const client = await getGraphClientSilent();
  if (!client) return { changed: false, missing };

  let changed = false;
  for (const row of linked) {
    let outlook: OutlookEventTimes | null = null;
    try {
      outlook = await client
        .api(`/me/events/${row.calendar_event_id}`)
        .select('start,end,isAllDay')
        .get();
    } catch (err) {
      if (isGraphNotFound(err)) missing.push(String(row.calendar_event_id));
      continue;
    }
    if (!outlook?.start?.dateTime || !outlook?.end?.dateTime) continue;
    const changes = outlookDateChanges(row, outlook);
    if (!changes) continue;
    const { error } = await directSupabase
      .from('project_events')
      .update({ ...changes, updated_at: new Date().toISOString(), ...(userId ? { updated_by: userId } : {}) })
      .eq('id', row.id);
    if (error) {
      console.warn('Failed to copy Outlook dates onto project event', error);
      continue;
    }
    changed = true;
  }
  return { changed, missing };
}
